import { Button, Flex, Heading, Modal, Paragraph, Span, Textarea } from "@ir/ui";
import { useCallback, useEffect, useRef, useState } from "react";

interface ApplyPromoMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  promoName: string;
  isApplying: boolean;
  showReversalReason: boolean;
  onConfirm: (reversalReason?: string) => void;
}

const ApplyPromoMatchModal = ({
  isOpen,
  onClose,
  promoName,
  isApplying,
  showReversalReason,
  onConfirm,
}: ApplyPromoMatchModalProps) => {
  const [reversalReason, setReversalReason] = useState("");
  const ignoreSpuriousCloseRef = useRef(false);

  // @ir/ui Modal calls onClose when internal onOpen state lags behind isOpen=true on open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    ignoreSpuriousCloseRef.current = true;
    const frameId = requestAnimationFrame(() => {
      ignoreSpuriousCloseRef.current = false;
    });
    return () => cancelAnimationFrame(frameId);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (ignoreSpuriousCloseRef.current) {
      return;
    }
    setReversalReason("");
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    const trimmedReason = reversalReason.trim();
    onConfirm(trimmedReason || undefined);
    setReversalReason("");
  }, [onConfirm, reversalReason]);

  const headerContent = (
    <Flex direction="column" gap={0.15} justifyContent="start" align="start">
      <Heading level={6} className="text-grey-700 font-bold mb-1">
        Approve &amp; Apply
      </Heading>
      <Paragraph className="text-grey-600 text-sm m-0">
        Promo <Span className="text-primary-600 font-bold">{promoName}</Span> will be applied to this deduction.
      </Paragraph>
    </Flex>
  );

  const footerContent = (
    <Flex direction="row" gap={1}>
      <Button variant="clear" color="grey" onPress={handleClose} isDisabled={isApplying}>
        Cancel
      </Button>
      <Button color="primary" onPress={handleConfirm} isDisabled={isApplying}>
        Approve &amp; Apply
      </Button>
    </Flex>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      zIndex={12}
      width={460}
      isDismissible={false}
      closable={true}
      headerContent={headerContent}
      footerContent={footerContent}
      classNames={{
        header: "p-4",
        body: "p-4 pt-0",
        footer: "px-4 py-4",
      }}
    >
      <Paragraph className="mb-2 text-grey-500 text-sm font-bold m-0">
        {showReversalReason ? "Reason for switching (optional)" : "Reason (optional)"}
      </Paragraph>
      <Textarea
        minRows={2}
        maxLength={100}
        showCharCount
        value={reversalReason}
        onChange={e => setReversalReason(e.target.value)}
      />
    </Modal>
  );
};

export default ApplyPromoMatchModal;
